import React, { useCallback, useMemo } from 'react';

import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import { Stack } from '@mui/material';

import { ReopenImpact } from '~/types/reopen';
import { ExtractedDateRange } from '~/types/stopsell';

import { formatDateRange } from '~/utils/stopsellDateUtils';

import EditableDateRow from './EditableDateRow';
import ReopenRateRow from './ReopenRateRow';

interface Props {
  date: ExtractedDateRange;
  dateIndex: number;
  impacts: Array<ReopenImpact>;
  catalogRoomRates: Array<{ id: string; name: string }>;
  deselectedBlockIds: Set<string>;
  disabled: boolean;
  onToggle: (dateIndex: number, value: boolean) => void;
  onEdit: (dateId: string, newRange: { start: string; end: string }) => void;
  onDelete: (dateId: string) => void;
  onBlockToggle: (blockId: string, selected: boolean) => void;
}

export default function ReopenExtractedDateRow({
  date,
  dateIndex,
  impacts,
  catalogRoomRates,
  deselectedBlockIds,
  disabled,
  onToggle,
  onEdit,
  onDelete,
  onBlockToggle,
}: Props) {
  const handleToggle = useCallback((v: boolean) => onToggle(dateIndex, v), [onToggle, dateIndex]);
  const handleEdit = useCallback((range: { start: string; end: string }) => onEdit(date.id, range), [onEdit, date.id]);
  const handleDelete = useCallback(() => onDelete(date.id), [onDelete, date.id]);

  const rateRows = useMemo(() => {
    const rateNameById = new Map(catalogRoomRates.map((r) => [r.id, r.name]));
    return impacts.flatMap((impact) =>
      impact.blackout.blocks.map((block) => ({
        blockId: block.id,
        rateName: rateNameById.get(block.room_rate_id) ?? block.room_rate_id,
        impact,
      })),
    );
  }, [impacts, catalogRoomRates]);

  return (
    <EditableDateRow
      start={date.start}
      end={date.end}
      selected={date.selected}
      disabled={disabled}
      onToggle={handleToggle}
      onEdit={handleEdit}
      onDelete={handleDelete}
      icon={<LockOpenOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
      chipSx={{ bgcolor: 'success.50', color: 'success.dark', fontSize: 11 }}
      editLabel={`Edit ${formatDateRange(date.start, date.end)}`}
      deleteLabel={`Delete ${formatDateRange(date.start, date.end)}`}
    >
      {rateRows.length > 0 && date.selected && (
        <Stack gap={0.5} sx={{ pl: 4 }}>
          {rateRows.map((row) => (
            <ReopenRateRow
              key={row.blockId}
              rateName={row.rateName}
              blockId={row.blockId}
              impact={row.impact}
              selected={!deselectedBlockIds.has(row.blockId)}
              onToggle={onBlockToggle}
            />
          ))}
        </Stack>
      )}
    </EditableDateRow>
  );
}
